/* eslint-disable consistent-default-export-name/default-import-match-filename */
import map from 'lodash/map';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  USER_PROPERTY__EMAIL,
  USER_PROPERTY__NAME,
  USER_PROPERTY__PHONE,
} from '../../../common/constants';
import { ADMIN_UPDATE_USER_PHONES } from '../../../common/permissions';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { apiCurrentUserUpdateProfile } from '../../../common/api/currentUser';
import settings from '../../../common/settings';
import FormFieldText from '../../../components/forms/FormFieldText';
import FormFieldPhoneNumber from '../../../components/forms/FormFieldPhoneNumber';
import FormItemReadOnly from '../../../components/forms/FormItemReadOnly';
import useForm from '../../../utils/useForm';
import { notifyError, notifySuccess } from '../../../utils/notify';
import Button from '../../../components/Button';
import UpdatePassword from '../../../components/dialogs/UpdatePassword';
import usePermission from '../../../utils/usePermission';
import CurrentUser from '../../../models/CurrentUser';
import { clearValue } from '../../../utils/sanitize';
import renderFormField from '../../../utils/renderFormField';

const {
  public: { country },
} = settings;

const UserProfileDetails = ({ disabledFields }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(CurrentUser.select.user());
  const canEditPhones = usePermission(ADMIN_UPDATE_USER_PHONES);
  const [isUpdatePasswordModalVisible, setIsUpdatePasswordModalVisible] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const handleOnUpdatePasswordModalOpen = () =>
    setIsUpdatePasswordModalVisible(true);
  const handleOnUpdatePasswordModalClose = () =>
    setIsUpdatePasswordModalVisible(false);
  const handleOnEdit = () => setIsEditing(true);
  const handleOnCancel = () => setIsEditing(false);
  const handleOnFormSubmitted = () => setIsFormSubmitting(false);

  const inputs = useMemo(
    () => ({
      [USER_PROPERTY__NAME]: {
        label: t('forms:name.label'),
        type: 'text',
        autoComplete: 'name',
        defaultValue: currentUser?.getFullName(),
        disabled:
          disabledFields.includes(USER_PROPERTY__NAME) || isFormSubmitting,
        schema: {
          type: 'string',
        },
      },
      [USER_PROPERTY__PHONE]: {
        label: t('forms:phone.label'),
        type: 'phone',
        defaultValue: currentUser?.getPhoneNumber(),
        disabled:
          !canEditPhones ||
          disabledFields.includes(USER_PROPERTY__PHONE) ||
          isFormSubmitting,
        schema: {
          type: 'string',
          format: 'phone',
          optional: true,
        },
      },
      [USER_PROPERTY__EMAIL]: {
        label: t('forms:email.label'),
        type: 'text',
        defaultValue: currentUser?.getEmailAddress(),
        disabled: true,
        schema: {
          type: 'string',
          optional: true,
        },
      },
      password: {
        label: t('forms:password.label'),
        type: 'text',
        defaultValue: '********',
        disabled: true,
        schema: {
          type: 'string',
          optional: true,
        },
      },
      alerts_via_sms: {
        label: 'Receive alerts via SMS',
        type: 'checkbox',
        defaultValue: (() => currentUser?.alertNotificationsPreference?.sms)(),
        disabled: false,
        schema: {
          type: 'boolean',
          optional: true,
        },
      },
      alerts_via_email: {
        label: 'Receive alerts via Email',
        type: 'checkbox',
        defaultValue: (() =>
          currentUser?.alertNotificationsPreference?.email)(),
        disabled: false,
        schema: {
          type: 'boolean',
          optional: true,
        },
      },
    }),
    [t, canEditPhones, currentUser, disabledFields, isFormSubmitting],
  );

  const { fields, handleSubmit } = useForm({
    inputs,
    onSubmit: (values) => {
      setIsFormSubmitting(true);
      const params = {};
      if (!disabledFields.includes(USER_PROPERTY__NAME)) {
        params.name = values.name || '';
      }

      if (!disabledFields.includes(USER_PROPERTY__PHONE)) {
        params.phone = values.phone || '';
      }

      if (!values.alerts_via_sms) {
        params.alertNotificationsPreference = {
          email: true,
          sms: false,
        };
      } else if (!values.alerts_via_email) {
        params.alertNotificationsPreference = {
          email: !values.phone ? true : values.alerts_via_email,
          sms: !!values.phone,
        };
      } else {
        params.alertNotificationsPreference = {
          email: values.alerts_via_email,
          sms: values.alerts_via_sms,
        };
      }

      dispatch(
        callMethod(apiCurrentUserUpdateProfile, clearValue(params, 'name')),
      )
        .then(() => {
          notifySuccess(t('confirmations:updateUser.success'))();
          handleOnCancel();
          handleOnFormSubmitted();
        })
        .catch((err) => {
          notifyError()(err);
          handleOnCancel();
          handleOnFormSubmitted();
        });
    },
  });

  const getField = (fieldName) => fields.find(({ name }) => name === fieldName);

  return (
    <>
      <form className="stack-6" onSubmit={handleSubmit}>
        <div className="cluster-4 justify-end">
          {isEditing ? (
            <div className="cluster-2">
              <Button htmlType="submit" type="ghost" loading={isFormSubmitting}>
                {t('save')}
              </Button>
              <Button
                type="ghost"
                onClick={handleOnCancel}
                disabled={isFormSubmitting}
              >
                {t('cancel')}
              </Button>
            </div>
          ) : (
            <Button type="ghost" onClick={handleOnEdit}>
              {t('edit')}
            </Button>
          )}
        </div>
        <div className="grid gap-6 grid-cols-2">
          {isEditing
            ? fields.map((field) => {
                if (
                  field.name === 'password' &&
                  currentUser?.isPasswordLoginDisabled()
                ) {
                  return null;
                }

                if (field.name === 'password') {
                  return (
                    <div className="cluster-2 items-end">
                      <div className="flex-1">
                        <FormFieldText
                          key={field.name}
                          input={field.input}
                          meta={field.meta}
                          id={field.id}
                          name={field.name}
                          type={field.type}
                          required={field.required}
                          autoComplete={field.autoComplete}
                          label={field.label}
                          placeholder={field.placeholder}
                          size={field.size}
                          disabled={field.disabled}
                          schema={field.schema}
                          isNewPresentation
                        />
                      </div>
                      <Button
                        onClick={handleOnUpdatePasswordModalOpen}
                        disabled={isFormSubmitting}
                      >
                        {t('update')}
                      </Button>
                    </div>
                  );
                }

                if (field.name === USER_PROPERTY__PHONE) {
                  return (
                    <div className="stack-1">
                      <FormFieldPhoneNumber
                        name={field.name}
                        label={field.label}
                        required={field.required}
                        disabled={field.disabled}
                        input={field.input}
                        meta={field.meta}
                        defaultCountry={country}
                      />
                      {renderFormField(getField('alerts_via_sms'))}
                    </div>
                  );
                }

                if (field.name === USER_PROPERTY__EMAIL) {
                  return (
                    <div className="stack-1">
                      <FormFieldText
                        key={field.name}
                        input={field.input}
                        meta={field.meta}
                        id={field.id}
                        name={field.name}
                        type={field.type}
                        required={field.required}
                        autoComplete={field.autoComplete}
                        label={field.label}
                        placeholder={field.placeholder}
                        size={field.size}
                        disabled={field.disabled}
                        schema={field.schema}
                        isNewPresentation
                      />
                      {renderFormField(getField('alerts_via_email'))}
                    </div>
                  );
                }

                if (
                  field.name === 'alerts_via_email' ||
                  field.name === 'alerts_via_sms'
                ) {
                  return null;
                }

                return (
                  <FormFieldText
                    key={field.name}
                    input={field.input}
                    meta={field.meta}
                    id={field.id}
                    name={field.name}
                    type={field.type}
                    required={field.required}
                    autoComplete={field.autoComplete}
                    label={field.label}
                    placeholder={field.placeholder}
                    size={field.size}
                    disabled={field.disabled}
                    schema={field.schema}
                    isNewPresentation
                  />
                );
              })
            : map(inputs, (input, key) => {
                if (
                  key === 'password' &&
                  currentUser?.isPasswordLoginDisabled()
                ) {
                  return null;
                }

                if (key === 'alerts_via_email' || key === 'alerts_via_sms') {
                  return null;
                }

                if (
                  key === USER_PROPERTY__EMAIL ||
                  key === USER_PROPERTY__PHONE
                ) {
                  let dynamicText = '';
                  if (
                    currentUser?.alertNotificationsPreference?.email &&
                    currentUser?.alertNotificationsPreference?.sms
                  ) {
                    dynamicText = 'Receiving alerts via Email and SMS';
                  } else if (
                    key === USER_PROPERTY__EMAIL &&
                    currentUser?.alertNotificationsPreference?.email
                  ) {
                    dynamicText = 'Receiving alerts via Email';
                  } else if (
                    key === USER_PROPERTY__PHONE &&
                    currentUser?.alertNotificationsPreference?.sms
                  ) {
                    dynamicText = 'Receiving alerts via SMS';
                  }
                  return (
                    <div className="stack-1">
                      <FormItemReadOnly key={key} label={input.label}>
                        <span>{input.defaultValue}</span>
                      </FormItemReadOnly>
                      <span>{dynamicText}</span>
                    </div>
                  );
                }

                return (
                  <FormItemReadOnly key={key} label={input.label}>
                    <span>{input.defaultValue}</span>
                  </FormItemReadOnly>
                );
              })}
        </div>
      </form>
      <UpdatePassword
        visible={isUpdatePasswordModalVisible}
        onClose={handleOnUpdatePasswordModalClose}
      />
    </>
  );
};

UserProfileDetails.propTypes = {
  disabledFields: PropTypes.arrayOf(PropTypes.string),
};

UserProfileDetails.defaultProps = {
  disabledFields: [],
};

export default UserProfileDetails;
